// A2PCaptureStep — the required carrier-registration step in Surf onboarding.
//
// Shown as a full-screen overlay (matching TalkTrackPreview) AFTER the tenant
// approves their talk track and BEFORE the activation ritual. It collects the
// fields Twilio needs for the secondary Customer Profile (business identity +
// authorized representative) and submits the immediate, submittable part of A2P:
//
//   create-twilio-subaccount  → dedicated per-tenant Twilio subaccount
//   create-customer-profile   → identity bundle, evaluated + submitted (pending-review)
//
// It does NOT register the brand — create-a2p-brand requires the profile to be
// 'twilio-approved' first (~5-10 days out). Submitting the profile starts that
// carrier review clock. The brand/campaign fire later (Settings wizard / future
// auto-advance). See C:\Users\thoma\.claude\plans\mighty-noodling-seal.md.
//
// The tenant is the authorized representative, so Twilio will email THEM about
// the registration — the card educates them about that up front (we no longer
// route those emails to a SurFox address).
import React, { useState, useEffect } from 'react';
import { Shield, Loader2, AlertCircle, Mail, CheckCircle2, Copy, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import supabase from '../../lib/supabaseClient';
import { readInvokeError } from '../../lib/invokeError';
import {
  formatEINForTwilio,
  formatEINForDisplay,
  formatPhoneForTwilio,
  industryToVertical,
  BUSINESS_TYPES,
  TWILIO_VERTICALS,
  US_STATES,
  OPT_IN_PRESETS,
} from '../../lib/a2pFormat';

// Twilio authorized_representative job_position is a STRICT enum — verified
// against Twilio's "Gather the Required Business Information" doc. "Owner" is
// NOT accepted (that belongs in the free-text business_title); use these exact
// values only or the customer profile is rejected.
const JOB_POSITIONS = ['CEO', 'Director', 'GM', 'VP', 'CFO', 'General Counsel', 'Other'];

const phoneDigits = (p) => (p || '').replace(/\D/g, '');

// A copyable block of consent text we hand the tenant to paste onto their site.
function ConsentCopyBlock({ title, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard blocked — user can select manually */ }
  };
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider text-slate-400">{title}</div>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200">
          <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-[12px] text-slate-200 leading-relaxed font-sans">{text}</pre>
    </div>
  );
}

export default function A2PCaptureStep({ pillars, onSuccess }) {
  const bo = pillars?.business_operations?.data || {};

  const [form, setForm] = useState({
    business_name: bo.company_name || '',
    business_type: 'LLC',
    business_registration_number: '',
    business_industry: industryToVertical(bo.industry),
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
    website: bo.website || '',
    privacy_policy_url: '',           // tenant-supplied — we verify this, not guess it
    auth_rep_first_name: bo.contact_first_name || '',
    auth_rep_last_name: '',
    auth_rep_email: '',
    auth_rep_phone: '',
    auth_rep_business_title: 'Owner', // free-text title — "Owner" is fine here
    auth_rep_job_position: 'CEO',     // strict Twilio enum (NOT "Owner")
    opt_in_preset: 'default',         // → message_flow string sent later as the campaign opt-in
  });
  const [submitting, setSubmitting] = useState(false);
  const [stepLabel, setStepLabel] = useState('');
  const [error, setError] = useState('');

  // Tier-1 consent gate: scan the tenant's site for SMS consent infrastructure
  // BEFORE we submit, so we don't file registrations carriers will reject (30896).
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);        // verify-sms-consent result
  const [consentAck, setConsentAck] = useState(false); // explicit override when scan can't confirm

  // Pre-fill the authorized-rep email from the signed-in user.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (active && user?.email) {
          setForm((f) => (f.auth_rep_email ? f : { ...f, auth_rep_email: user.email }));
        }
      } catch (_) { /* best-effort prefill */ }
    })();
    return () => { active = false; };
  }, []);

  // Pull the consent pre-scan fired during onboarding (in parallel with the website
  // scrape) so findings show instantly. Only adopt it if it matches the website
  // we're about to submit; otherwise the tenant runs a fresh check.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke('verify-sms-consent', { body: { mode: 'peek' } });
        if (!active || !data?.cached || !data.scan) return;
        const norm = (u) => (u || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();
        if (form.website && norm(data.scan.website) && norm(data.scan.website) === norm(form.website)) {
          setScan(data.scan);
        }
      } catch (_) { /* best-effort; tenant can still run a manual check */ }
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Re-checking invalidates a prior pass + any acknowledgment.
  async function runConsentScan() {
    setScanning(true);
    setScan(null);
    setConsentAck(false);
    try {
      const { data, error: e } = await supabase.functions.invoke('verify-sms-consent', {
        body: { website: form.website, business_name: form.business_name },
      });
      if (e) throw new Error(await readInvokeError(e, 'Could not run the consent check.'));
      setScan(data);
    } catch (err) {
      setScan({ success: false, scanned: false, ready: false, message: err.message || 'Could not run the consent check.' });
    } finally {
      setScanning(false);
    }
  }

  // Gate: may submit only when the scan confirmed consent, or the tenant has
  // explicitly acknowledged they've installed the consent copy themselves.
  const consentCleared = !!(scan?.ready || consentAck);

  const isSoleProp = form.business_type === 'SOLE_PROPRIETORSHIP';
  const taxIdLabel = isSoleProp ? 'SSN' : 'EIN';

  function validate() {
    const required = [
      ['business_name', 'Business legal name'],
      ['business_registration_number', taxIdLabel],
      ['business_industry', 'Industry'],
      ['street', 'Street'],
      ['city', 'City'],
      ['state', 'State'],
      ['postal_code', 'ZIP'],
      ['phone', 'Business phone'],
      ['auth_rep_first_name', 'Your first name'],
      ['auth_rep_last_name', 'Your last name'],
      ['auth_rep_email', 'Your email'],
      ['auth_rep_phone', 'Your mobile'],
    ];
    for (const [key, label] of required) {
      if (!String(form[key] || '').trim()) return `${label} is required.`;
    }
    if (formatEINForTwilio(form.business_registration_number).length !== 9) {
      return `${taxIdLabel} must be 9 digits.`;
    }
    const bp = phoneDigits(form.phone);
    if (bp.length !== 10 && !(bp.length === 11 && bp.startsWith('1'))) {
      return 'Business phone must be a 10-digit US number.';
    }
    const rp = phoneDigits(form.auth_rep_phone);
    if (rp.length !== 10 && !(rp.length === 11 && rp.startsWith('1'))) {
      return 'Your mobile must be a 10-digit US number.';
    }
    if (!/^\S+@\S+\.\S+$/.test(form.auth_rep_email)) {
      return 'Enter a valid email for the authorized representative.';
    }
    if (!/^\d{5}(-\d{4})?$/.test(String(form.postal_code).trim())) {
      return 'Enter a valid US ZIP code.';
    }
    return null;
  }

  async function handleSubmit() {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    if (!consentCleared) {
      setError('Verify your website has SMS consent set up (or confirm it below) before submitting. Carriers reject registrations without it.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      // 1. Ensure the dedicated per-tenant Twilio subaccount exists (idempotent).
      setStepLabel('Setting up your carrier account…');
      const { data: subData, error: subErr } = await supabase.functions.invoke('create-twilio-subaccount', {
        body: { legalBusinessName: form.business_name },
      });
      if (subErr) throw new Error(await readInvokeError(subErr, 'Could not set up your carrier account.'));
      if (!subData?.success) throw new Error(subData?.message || 'Could not set up your carrier account.');

      // 2. Create + submit the secondary Customer Profile (identity review).
      setStepLabel('Filing your business with the carriers…');
      const payload = {
        business_name: form.business_name.trim(),
        business_type: form.business_type,
        business_registration_number: formatEINForTwilio(form.business_registration_number),
        business_industry: form.business_industry,
        street: form.street.trim(),
        city: form.city.trim(),
        state: form.state,
        postal_code: String(form.postal_code).trim(),
        country: form.country || 'US',
        email: form.auth_rep_email.trim(),
        phone: formatPhoneForTwilio(form.phone),
        website: form.website?.trim() || '',
        privacy_policy_url: form.privacy_policy_url?.trim() || '',
        auth_rep_first_name: form.auth_rep_first_name.trim(),
        auth_rep_last_name: form.auth_rep_last_name.trim(),
        auth_rep_email: form.auth_rep_email.trim(),
        auth_rep_phone: formatPhoneForTwilio(form.auth_rep_phone),
        auth_rep_business_title: form.auth_rep_business_title.trim() || 'Owner',
        auth_rep_job_position: form.auth_rep_job_position || 'CEO',
        // Consent description for the future A2P campaign (stored on the profile now).
        opt_in_method: (OPT_IN_PRESETS.find((p) => p.value === form.opt_in_preset) || OPT_IN_PRESETS[0]).flow,
      };
      const { data: profileData, error: profileErr } = await supabase.functions.invoke('create-customer-profile', {
        body: payload,
      });
      if (profileErr) throw new Error(await readInvokeError(profileErr, 'Carrier rejected your business details.'));
      if (!profileData?.success) throw new Error(profileData?.message || 'Carrier rejected your business details. Double-check your EIN and address.');

      // Submitted for review — hand control back to onboarding to finish.
      onSuccess?.();
    } catch (err) {
      console.error('[A2PCaptureStep] submit failed:', err);
      setError(err.message || 'Something went wrong submitting your registration.');
    } finally {
      setSubmitting(false);
      setStepLabel('');
    }
  }

  const inputCls = 'w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500';
  const labelCls = 'block text-[11px] uppercase tracking-wider text-slate-400 mb-1';

  return (
    <div className="overlay-in fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-md flex items-center justify-center px-6 py-8 overflow-y-auto">
      <div className="w-full max-w-3xl bg-slate-900 border border-slate-700/80 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10 my-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cyan-400/80 mb-2">
            <Shield size={13} /> Carrier registration
          </div>
          <div className="text-2xl font-semibold text-slate-100">Last step before you can text real leads.</div>
          <div className="text-xs text-slate-500 mt-2 max-w-xl mx-auto leading-relaxed">
            Carriers require every business to register before sending SMS. We file this for you now —
            approval usually takes about 7-10 days, and we'll let you know the moment you're cleared to send.
          </div>
        </div>

        {/* Twilio email education */}
        <div className="mb-6 flex items-start gap-3 px-4 py-3 rounded-lg bg-cyan-900/20 border border-cyan-700/40">
          <Mail size={16} className="text-cyan-300 mt-0.5 flex-none" />
          <div className="text-xs text-cyan-100/90 leading-relaxed">
            You'll get one or two emails from <span className="font-semibold">Twilio</span> about this
            registration — they're legitimate and expected. You don't need to do anything with them unless
            we ask; we handle the rest.
          </div>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-900/30 border border-red-700/60 text-sm text-red-200">
            <AlertCircle size={16} className="mt-0.5 flex-none" />
            <span>{error}</span>
          </div>
        )}

        {/* Business identity */}
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Your business</div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="col-span-2">
            <label className={labelCls}>Business legal name</label>
            <input className={inputCls} value={form.business_name} onChange={(e) => set('business_name', e.target.value)} placeholder="As it appears on your tax docs" />
          </div>
          <div>
            <label className={labelCls}>Business structure</label>
            <select className={inputCls} value={form.business_type} onChange={(e) => set('business_type', e.target.value)}>
              {BUSINESS_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>{taxIdLabel}</label>
            <input
              className={inputCls}
              value={formatEINForDisplay(form.business_registration_number, form.business_type)}
              onChange={(e) => set('business_registration_number', e.target.value)}
              placeholder={isSoleProp ? 'XXX-XX-XXXX' : 'XX-XXXXXXX'}
              inputMode="numeric"
            />
          </div>
          <div>
            <label className={labelCls}>Industry</label>
            <select className={inputCls} value={form.business_industry} onChange={(e) => set('business_industry', e.target.value)}>
              {TWILIO_VERTICALS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Business phone</label>
            <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(512) 555-0142" inputMode="tel" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Website <span className="text-slate-600 normal-case">(optional)</span></label>
            <input className={inputCls} value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://…" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Privacy policy URL</label>
            <input
              className={inputCls}
              value={form.privacy_policy_url}
              onChange={(e) => set('privacy_policy_url', e.target.value)}
              placeholder="https://yoursite.com/privacy-policy"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Carriers require your privacy policy to cover SMS.{' '}
              {form.website ? (
                <>
                  Need the link?{' '}
                  <a
                    href={/^https?:\/\//i.test(form.website) ? form.website : `https://${form.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 underline"
                  >
                    Open your site ↗
                  </a>{' '}
                  and copy your Privacy Policy link.
                </>
              ) : (
                'Paste the link to your Privacy Policy page.'
              )}
            </div>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>How do your leads opt in?</label>
            <select className={inputCls} value={form.opt_in_preset} onChange={(e) => set('opt_in_preset', e.target.value)}>
              {OPT_IN_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">How recipients consented to receive your texts — carriers require this.</div>
          </div>
        </div>

        {/* Address */}
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Business address</div>
        <div className="grid grid-cols-6 gap-4 mb-4">
          <div className="col-span-6">
            <label className={labelCls}>Street</label>
            <input className={inputCls} value={form.street} onChange={(e) => set('street', e.target.value)} placeholder="123 Main St" />
          </div>
          <div className="col-span-3">
            <label className={labelCls}>City</label>
            <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} />
          </div>
          <div className="col-span-1">
            <label className={labelCls}>State</label>
            <select className={inputCls} value={form.state} onChange={(e) => set('state', e.target.value)}>
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>ZIP</label>
            <input className={inputCls} value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} placeholder="78701" inputMode="numeric" />
          </div>
        </div>

        {/* Authorized representative */}
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Authorized representative</div>
        <div className="text-[11px] text-slate-500 mb-3">This is you — the person who can vouch for the business with carriers.</div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className={labelCls}>First name</label>
            <input className={inputCls} value={form.auth_rep_first_name} onChange={(e) => set('auth_rep_first_name', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Last name</label>
            <input className={inputCls} value={form.auth_rep_last_name} onChange={(e) => set('auth_rep_last_name', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input className={inputCls} value={form.auth_rep_email} onChange={(e) => set('auth_rep_email', e.target.value)} placeholder="you@company.com" />
          </div>
          <div>
            <label className={labelCls}>Mobile</label>
            <input className={inputCls} value={form.auth_rep_phone} onChange={(e) => set('auth_rep_phone', e.target.value)} placeholder="(512) 555-0142" inputMode="tel" />
          </div>
          <div>
            <label className={labelCls}>Title</label>
            <input className={inputCls} value={form.auth_rep_business_title} onChange={(e) => set('auth_rep_business_title', e.target.value)} placeholder="Owner" />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select className={inputCls} value={form.auth_rep_job_position} onChange={(e) => set('auth_rep_job_position', e.target.value)}>
              {JOB_POSITIONS.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>
        </div>

        {/* SMS consent gate — carriers verify this on your live site (error 30896) */}
        <div className="mb-6 rounded-xl border border-slate-700/80 bg-slate-950/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-slate-400">SMS consent check</span>
              {scan?.fromCache && <span className="text-[10px] text-slate-500">checked during setup</span>}
            </div>
            <button
              type="button"
              onClick={runConsentScan}
              disabled={scanning}
              className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200 disabled:opacity-50"
            >
              {scanning ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {scan ? 'Re-check' : 'Check my website'}
            </button>
          </div>
          <div className="text-[11px] text-slate-500 leading-relaxed mb-3">
            Before we file your registration, carriers require your website to show how leads consent to texts.
            We check your site so this doesn&apos;t get rejected days later.
          </div>

          {scanning && (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 size={14} className="animate-spin" /> Checking {form.website || 'your website'}…
            </div>
          )}

          {scan && !scanning && (
            scan.ready ? (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-emerald-900/25 border border-emerald-700/50 text-sm text-emerald-200">
                <ShieldCheck size={16} className="mt-0.5 flex-none" />
                <span>Your website has the SMS consent language carriers require. You&apos;re good to submit.</span>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-900/25 border border-amber-700/50 text-sm text-amber-100">
                  <ShieldAlert size={16} className="mt-0.5 flex-none" />
                  <span>{scan.message || 'We couldn’t confirm SMS consent on your site. Add the copy below, then re-check.'}</span>
                </div>
                {scan.checkboxCopy && (
                  <ConsentCopyBlock title="Add this checkbox to your lead form" text={scan.checkboxCopy} />
                )}
                {scan.privacyCopy && (
                  <ConsentCopyBlock title="Add this section to your Privacy Policy" text={scan.privacyCopy} />
                )}
                <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentAck}
                    onChange={(e) => setConsentAck(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>I&apos;ve added this consent language to my website (or will before sending). I understand carriers verify it and may reject the registration if it&apos;s missing.</span>
                </label>
              </div>
            )
          )}

          {!scan && !scanning && (
            <div className="text-[11px] text-slate-500">
              Run the check above to continue. If you don&apos;t have a website with a lead form, tick the box after checking to proceed with the consent copy we provide.
            </div>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || !consentCleared}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {submitting
            ? <><Loader2 size={16} className="animate-spin" /> {stepLabel || 'Submitting…'}</>
            : <><CheckCircle2 size={16} /> Submit registration &amp; finish setup</>}
        </button>
        <div className="text-center text-[11px] text-slate-600 mt-3">
          By submitting, you confirm you're authorized to register this business for SMS.
        </div>
      </div>
    </div>
  );
}
